import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Grid,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, DeveloperStats } from "../types";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";

interface DeveloperStatsProps {
  requests: EndpointRequest[];
  currentUser: string;
}

export const DeveloperStatsComponent: React.FC<DeveloperStatsProps> = ({
  requests,
  currentUser,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const primaryColor = colors.primary[400];
  
  const [stats, setStats] = useState<DeveloperStats>({
    username: currentUser,
    totalRequests: 0,
    currentStreak: 0,
    longestStreak: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Fetch total requests from API
  const loadTotalRequests = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      // Update total requests from API
      setStats((prev) => ({
        ...prev,
        totalRequests: response.data.total_requests || 0,
      }));
    } catch (error: unknown) {
      console.error("Error loading total requests:", error);
      // Fallback to calculating from requests
      const userRequests = requests.filter(
        (r) => r.createdBy?.toLowerCase() === currentUser.toLowerCase()
      );
      setStats((prev) => ({
        ...prev,
        totalRequests: userRequests.length,
      }));
    } finally {
      setLoadingStats(false);
    }
  }, [currentUser, requests]);

  // Fetch awarded points for the current developer from the admin leaderboard
  const loadDeveloperAwards = useCallback(async () => {
    try {
      const response = await apiService.get<{
        date_awarded: string;
        leaderboard: Array<{
          user_id: number;
          name: string;
          requests_count: number;
          awarded_points: number;
        }>;
      }>("/api/endpoint-request/endpoint-requests/daily-leaderboard/");

      const developerEntry = response.data.leaderboard.find(
        (dev) => dev.name.toLowerCase() === currentUser.toLowerCase()
      );

      setStats((prev) => ({
        ...prev,
        points: developerEntry?.awarded_points ?? 0,
      }));
    } catch (error: unknown) {
      console.error("Error loading developer awards:", error);
    }
  }, [currentUser]);

  const calculateStreaks = useCallback(() => {
    const userRequests = requests.filter(
      (r) => r.createdBy?.toLowerCase() === currentUser.toLowerCase()
    );

    if (userRequests.length === 0) {
      setStats((prev) => ({
        ...prev,
        currentStreak: 0,
      }));
      return;
    }

    // Sort requests by date
    const sortedRequests = [...userRequests].sort(
      (a, b) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime()
    );

    // Calculate current streak (from most recent request)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const mostRecentRequest = sortedRequests[sortedRequests.length - 1];
    const mostRecentDate = new Date(mostRecentRequest.requestDate);
    mostRecentDate.setHours(0, 0, 0, 0);

    const daysSinceLastRequest = Math.floor(
      (today.getTime() - mostRecentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    let currentStreak = 0;
    if (daysSinceLastRequest === 0 || daysSinceLastRequest === 1) {
      // Check if there's an active streak
      let streakCount = 1;
      let checkDate = new Date(mostRecentDate);
      
      for (let i = sortedRequests.length - 2; i >= 0; i--) {
        const prevDate = new Date(sortedRequests[i].requestDate);
        prevDate.setHours(0, 0, 0, 0);
        const diff = Math.floor((checkDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diff === 1) {
          streakCount++;
          checkDate = prevDate;
        } else if (diff > 1) {
          break;
        }
      }
      
      currentStreak = streakCount;
    }

    // Load points from localStorage
    setStats((prev) => ({
      ...prev,
      currentStreak,
      lastRequestDate: mostRecentRequest.requestDate,
    }));
  }, [requests, currentUser]);

  useEffect(() => {
    loadTotalRequests();
    loadDeveloperAwards();
  }, [loadTotalRequests, loadDeveloperAwards]);

  useEffect(() => {
    calculateStreaks();
  }, [calculateStreaks]);

  // Listen for points updates
  useEffect(() => {
    const handlePointsUpdate = (event: CustomEvent) => {
      if (event.detail?.username === currentUser) {
        calculateStreaks();
        loadDeveloperAwards();
      }
    };

    window.addEventListener(
      "developer-points-updated",
      handlePointsUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        "developer-points-updated",
        handlePointsUpdate as EventListener
      );
    };
  }, [calculateStreaks, currentUser, loadDeveloperAwards]);

  const getStreakColor = (streak: number) => {
    if (streak >= 7) return "error";
    if (streak >= 3) return "warning";
    return "info";
  };

  const getStreakMessage = (streak: number) => {
    if (streak === 0) return "Start your streak today!";
    if (streak === 1) return "Great start! Keep it going!";
    if (streak < 7) return `${streak} day streak!`;
    if (streak < 14) return `${streak} day streak! Amazing!`;
    return `${streak} day streak! Legendary!`;
  };

  return (
    <Card sx={{ mb: 3, backgroundColor: primaryColor, color: "white" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 56, height: 56 }}>
            <TrophyIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Your Developer Stats
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Track your endpoint request streak
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight="bold">
                {loadingStats ? "..." : stats.totalRequests}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Requests
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <FireIcon sx={{ fontSize: 40 }} />
                <Typography variant="h3" fontWeight="bold">
                  {stats.currentStreak}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Current Streak
              </Typography>
              <Chip
                icon={<FireIcon />}
                label={getStreakMessage(stats.currentStreak)}
                size="small"
                color={getStreakColor(stats.currentStreak)}
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <StarIcon sx={{ fontSize: 40, color: "warning.main" }} />
                <Typography variant="h3" fontWeight="bold" color="warning.main">
                  {stats.points || 0}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Streak Points
              </Typography>
              {(stats.points || 0) > 0 && (
                <Chip
                  icon={<StarIcon />}
                  label={`${stats.points} points`}
                  size="small"
                  color="warning"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {stats.currentStreak > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption">Streak Progress</Typography>
              <Typography variant="caption">
                {stats.currentStreak} day{stats.currentStreak !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((stats.currentStreak / 7) * 100, 100)}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: "rgba(255,255,255,0.2)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.primary[900],
                },
              }}
            />
            {stats.currentStreak < 7 && (
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>
                {7 - stats.currentStreak} more day{7 - stats.currentStreak !== 1 ? "s" : ""} to reach 7-day milestone!
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

